import React, { useEffect, useState, useCallback } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronRight } from "lucide-react";

export default function BrandCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [subs, setSubs] = useState([]);
  const [subInput, setSubInput] = useState("");

  const load = useCallback(async () => {
    try { const r = await staffApi.get("/categories"); setCats(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setName(""); setSubs([]); setSubInput(""); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setSubs(c.subcategories || []); setSubInput(""); setOpen(true); };

  const addSub = () => {
    const v = subInput.trim();
    if (!v) return;
    setSubs((s) => [...s, { id: `tmp-${Date.now()}-${Math.random()}`, name: v, _new: true }]);
    setSubInput("");
  };
  const removeSub = async (sub) => {
    if (editing && !sub._new) {
      try { await staffApi.delete(`/categories/${editing.id}/subcategories/${sub.id}`); }
      catch (e) { toast.error(formatApiError(e)); return; }
    }
    setSubs((s) => s.filter((x) => x.id !== sub.id));
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      if (editing) {
        await staffApi.put(`/categories/${editing.id}`, { name: name.trim() });
        for (const s of subs) {
          if (s._new) await staffApi.post(`/categories/${editing.id}/subcategories`, { name: s.name });
        }
      } else {
        await staffApi.post("/categories", { name: name.trim(), subcategories: subs.map((s) => s.name) });
      }
      toast.success("Category saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeCategory = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await staffApi.delete(`/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="categories-title">Categories</h1>
          <p className="text-sm text-muted-foreground">Organise your service catalog. Categories cannot be deleted while services still reference them.</p>
        </div>
        <Button onClick={openNew} data-testid="new-category-btn" className="gap-1"><Plus className="w-4 h-4" /> New category</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {cats.length === 0 && <Card className="rounded-lg sm:col-span-2 lg:col-span-3"><CardContent className="p-8 text-center text-sm text-muted-foreground">No categories yet.</CardContent></Card>}
        {cats.map((c) => {
          const subCount = (c.subcategories || []).length;
          return (
            <div
              key={c.id}
              className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-white px-3.5 py-2.5 hover:border-primary/30 transition-colors"
              data-testid={`cat-card-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate text-sm">{c.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {subCount === 0 ? "No subcategories" : `${subCount} subcategor${subCount === 1 ? "y" : "ies"}`}
                </p>
              </div>
              <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeCategory(c)} data-testid={`delete-cat-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div><Label>Category name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Injectables" data-testid="cat-name-input" autoFocus /></div>
            <div>
              <Label>Subcategories</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <Input value={subInput} onChange={(e) => setSubInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }} placeholder="e.g. Botox, Fillers..." data-testid="sub-input" />
                <Button type="button" onClick={addSub} data-testid="add-sub-btn"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {subs.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs" data-testid={`dialog-sub-${s.name}`}>
                    <ChevronRight className="w-3 h-3 opacity-50" /> {s.name}
                    <button onClick={() => removeSub(s)} className="opacity-40 hover:opacity-100"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {subs.length === 0 && <p className="text-xs text-muted-foreground">Add as many as you wish.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-category-btn">Finish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
